import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, IconButton, Avatar, Slide, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import generateGroqAssistantContent from '../../../utils/groqAssistantClient';
import { stageCatalog } from './popupQuote/catalog';
import { FAQ_HITPOLY } from '../../../utils/faq_hitpoly';
import { VIRTUAL_ASSISTANT_API_CONFIG } from './virtualAssistant/assistantApiConfig';
import { buildAssistantKnowledgePrompt } from './virtualAssistant/assistantPlaybook';
import {
  createEmptyAssistantMemory,
  loadAssistantMemory,
  saveAssistantMemory,
  clearAssistantMemory,
  saveChatMessage,
} from './virtualAssistant/assistantMemory';
import { useAuth } from '../../../context/AuthContext';
import {
  createInitialAnswers,
  inferFieldsFromMessage,
  getMissingFields,
  mergeAnswers,
  buildAssistantSnapshot,
  buildAssistantMetrics,
  buildDeterministicReply,
  buildFallbackDiagnosis,
  hasEnoughForDiagnosis,
  determineJourneyStage,
  sanitizeAnswers,
  FIELD_LABELS,
  getStageConfig,
  getConversationChecklist,
  cleanText,
} from './virtualAssistant/assistantBrain';

const AGENT_PERSONAS = [
  { name: 'Laura', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=5', gender: 'female' },
  { name: 'Carlos', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=11', gender: 'male' },
  { name: 'Ana', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=9', gender: 'female' },
  { name: 'Diego', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=8', gender: 'male' },
  { name: 'Sofía', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=10', gender: 'female' },
  { name: 'Javier', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=12', gender: 'male' },
  { name: 'Elena', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=16', gender: 'female' },
  { name: 'Ricardo', role: 'Estratega de Marketing Digital', avatar: 'https://i.pravatar.cc/150?img=13', gender: 'male' }
];

const parseAiPayload = (rawText) => {
  if (!rawText) return null;
  const cleaned = rawText.replace(/```json|```/gi, '').trim();
  try {
    return JSON.parse(cleaned);
  } catch (error) {
    const firstBrace = cleaned.indexOf('{');
    const lastBrace = cleaned.lastIndexOf('}');

    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      try {
        return JSON.parse(cleaned.slice(firstBrace, lastBrace + 1));
      } catch (innerError) {
        return null;
      }
    }

    return null;
  }
};

const shellSx = {
  height: '100%',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  padding: { xs: '0px', md: '0px' },
};

const chatWindowSx = {
  width: '100%',
  height: '100%',
  backgroundColor: '#ffffff',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  overflow: 'hidden',
};

const headerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  padding: { xs: '14px 16px', md: '18px 24px' },
  borderBottom: '1px solid rgba(15,23,42,0.08)',
};

const bodySx = {
  overflowY: 'auto',
  padding: { xs: '18px 14px', md: '24px' },
  backgroundColor: '#ffffff',
};

const composerSx = {
  padding: { xs: '14px', md: '18px 24px 20px' },
  borderTop: '1px solid rgba(15,23,42,0.08)',
  backgroundColor: '#ffffff',
};

const assistantBubbleSx = {
  maxWidth: '820px',
  width: 'fit-content',
  backgroundColor: '#f6f7fb',
  border: '1px solid rgba(15,23,42,0.06)',
  color: '#14213d',
  borderRadius: '20px 20px 20px 6px',
  padding: '14px 16px',
};

const userBubbleSx = {
  maxWidth: '820px',
  width: 'fit-content',
  marginLeft: 'auto',
  backgroundColor: '#111111',
  color: '#ffffff',
  borderRadius: '20px 20px 6px 20px',
  padding: '14px 16px',
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const QUICK_REPLIES = [
  "🎯 Quiero campañas en redes",
  "💻 Necesito una web",
  "🤖 Automatizar mi negocio",
  "👋 Sólo investigaba"
];

const PopupCapturaLeads = ({ open, onClose }) => {
  const endRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [answers, setAnswers] = useState(createInitialAnswers());
  const [draft, setDraft] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [hasConclusion, setHasConclusion] = useState(false);
  const [providerCooldownUntil, setProviderCooldownUntil] = useState(0);
  const [typingDots, setTypingDots] = useState('.');
  const [activePersona, setActivePersona] = useState(AGENT_PERSONAS[0]);
  const [sessionId, setSessionId] = useState(null);
  const { user } = useAuth();

  const seedInitialConversation = useCallback(async () => {
    setIsThinking(true);
    await sleep(7000); 
    const initialAnswers = createInitialAnswers();
    
    pushAssistantMessage(
      `Hola, un gusto saludarle. Soy ${activePersona.name}, su asesor especializado en Hitpoly. ¿Con quién tengo el gusto de hablar? Me gustaría saber su nombre para asesorarle mejor.`,
      initialAnswers
    );
    setIsThinking(false);
  }, [activePersona?.name]);

  useEffect(() => {
    if (!open) return;

    const initializeChat = async () => {
      const memory = await loadAssistantMemory(user?.id);
      
      setSessionId(memory.sessionId);

      // 1. Restaurar o elegir Persona
      let personaToUse = memory.persona;
      if (!personaToUse) {
        const sessionCount = parseInt(localStorage.getItem('hitpoly_assistant_session_count') || '0', 10);
        const nextCount = sessionCount + 1;
        localStorage.setItem('hitpoly_assistant_session_count', nextCount.toString());
        const index = sessionCount % AGENT_PERSONAS.length;
        const basePersona = AGENT_PERSONAS[index];
        personaToUse = { ...basePersona, name: `${basePersona.name} ${nextCount}` };
      }
      setActivePersona(personaToUse);

      // 2. Restaurar Historia
      const hasHistory = Array.isArray(memory.messages) && memory.messages.length > 0;
      if (hasHistory) {
        setMessages(memory.messages.map((m, i) => ({ ...m, id: m.id || `restored-${i}-${m.role}` })));
        setAnswers(sanitizeAnswers({ ...createInitialAnswers(), ...(memory.answers || {}) }));
        setHasConclusion(Boolean(memory.diagnosis));
        return;
      }

      // 3. Iniciar Nueva Conversación
      setMessages([]);
      setAnswers(createInitialAnswers());
      setHasConclusion(false);
      
      const newMemory = await saveAssistantMemory({
        ...createEmptyAssistantMemory(),
        messages: [],
        answers: createInitialAnswers(),
        persona: personaToUse,
        sessionId: memory.sessionId // Reusamos el sessionId obtenido de la API
      }, user?.id);

      seedInitialConversation();
    };

    initializeChat();
  }, [open, user?.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isThinking]);

  useEffect(() => {
    if (!isThinking) {
      setTypingDots('.');
      return undefined;
    }

    const interval = setInterval(() => {
      setTypingDots((prev) => (prev.length >= 3 ? '.' : `${prev}.`));
    }, 340);

    return () => clearInterval(interval);
  }, [isThinking]);

  const getTypingDelay = (text, extra = 0) => {
    const chars = String(text || '').length;
    // Cadencia lenta y humana: entre 2 y 5 segundos segun longitud
    return Math.min(8000, Math.max(2500, chars * 28 + extra));
  };

  const syncMemory = async (nextMessages, nextAnswers, diagnosis = null) => {
    await saveAssistantMemory({
      messages: nextMessages,
      answers: nextAnswers,
      diagnosis,
      persona: activePersona,
      sessionId
    }, user?.id);
  };

  const pushAssistantMessage = (text, nextAnswers = answers, diagnosis = null) => {
    const safeText = typeof text === 'string' 
      ? text 
      : (typeof text === 'object' && text !== null
          ? `${text.title || text.name || text.mensaje || ''} ${text.actions || text.details || ''} ${text.expectedImpact || ''}`.trim() || JSON.stringify(text)
          : String(text || ''));

    // Guardar mensaje en DB
    saveChatMessage(sessionId, 'assistant', safeText);

    setMessages((prev) => {
      const nextMessages = [
        ...prev,
        {
          role: 'assistant',
          id: `assistant-${prev.length + 1}`,
          text: safeText,
        },
      ];
      syncMemory(nextMessages, nextAnswers, diagnosis);
      return nextMessages;
    });
  };

  const extractRetryMs = (message) => {
    const secondsMatch = String(message || '').match(/retry in\s+(\d+(?:\.\d+)?)s/i);
    if (secondsMatch) {
      return Math.ceil(Number(secondsMatch[1]) * 1000);
    }
    return VIRTUAL_ASSISTANT_API_CONFIG.providerRetryCooldownMs || 8000;
  };

  const runProviderWithFallbackModels = async (prompt, temperature, preferredModel, mode = 'turn') => {
    const configuredModels = VIRTUAL_ASSISTANT_API_CONFIG.fallbackModels?.length
      ? VIRTUAL_ASSISTANT_API_CONFIG.fallbackModels
      : [VIRTUAL_ASSISTANT_API_CONFIG.turnModel || VIRTUAL_ASSISTANT_API_CONFIG.diagnosisModel];
    const models = [preferredModel, ...configuredModels.filter((model) => model !== preferredModel)].filter(Boolean);

    let lastError = null;

    for (const model of models) {
      try {
        

        const response = await generateGroqAssistantContent(prompt, {
          model,
          temperature,
        });

        return { raw: response.text, model: response.model || model };
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Cloudflare Workers AI no devolvio respuesta.');
  };

  const buildTurnPrompt = (userText, nextAnswers) => {
    const stageKey = determineJourneyStage(nextAnswers);
    const config = getStageConfig(nextAnswers);
    const knowledgePrompt = buildAssistantKnowledgePrompt(stageKey);
    const checklist = getConversationChecklist(nextAnswers);
    const userName = nextAnswers.userName || 'el cliente';
    const isInvestigating = nextAnswers.isInvestigating === 'true';
    const knowsTargetService = (nextAnswers.knowsTargetService || '').toLowerCase();
    const isDirectContactFlow = knowsTargetService === 'si' || knowsTargetService === 'sí' || knowsTargetService === 'yes';

    let missionBlock = '';

    if (isInvestigating) {
      if (!nextAnswers.userName) {
        missionBlock = `### [BLOQUE 2 — MISIÓN: OBTENER NOMBRE]
El usuario solo está investigando. 
Tu UNICO objetivo ahora mismo es obtener su nombre para no hablar con un desconocido.
Dile que estás para servirle y responder cualquier duda sobre Hitpoly o estrategias digitales, pero pregúntale cómo se llama.`;
      } else {
        missionBlock = `### [BLOQUE 2 — MISIÓN: FAQ / SOPORTE]
El usuario "${userName}" está en modo investigador.
Tu UNICO objetivo es responder a sus preguntas y aportar valor.
---
=== INICIO FAQ HITPOLY ===
${FAQ_HITPOLY}
=== FIN FAQ HITPOLY ===`;
      }
    } else if (isDirectContactFlow) {
      missionBlock = `### [BLOQUE 2 — MISIÓN: CONTACTO DIRECTO]
EL USUARIO YA SABE QUÉ SERVICIO QUIERE. 
Tu UNICO objetivo ahora es obtener su Correo y WhatsApp para que un humano lo contacte de inmediato.
1. Si falta email o phone, pídelos con urgencia y calidez.
2. Si ya tienes ambos, confirma: "Perfecto, le he pasado tus datos al equipo. Se pondrán en contacto contigo de inmediato. ¿Hay algo más en lo que te pueda ayudar?".
3. Si el usuario responde que NO a ese "algo más", el sistema detectará el fin de la sesión enviando un mensaje final cordial.`;
    } else {
      missionBlock = `### [BLOQUE 2 — MISIÓN DE ESTE TURNO]
Estamos en la etapa: ${config.label}.
Tu objetivo es avanzar naturalmente hacia el próximo dato del checklist.

IMPORTANTE: Antes de pedir detalles técnicos de marca o presupuestos, ASEGÚRATE de saber a qué se dedican (actividad del negocio).

CHECKLIST DE DATOS PENDIENTES (sólo los que faltan):
${checklist || '¡Todos los datos están completos! Dale un breve resumen de lo recabado y pregúntale si desea corregir algún número antes del diagnóstico.'}`;
    }

    let rulesBlock = `REGLAS DE CONVERSACIÓN CRÍTICAS:
1. **ANTI-REDUNDANCIA — REGLA DE ORO**: Revisa el JSON de datos del cliente en el BLOQUE 3. Si un dato ya está presente (ej: "no tengo web", "presupuesto 500"), ¡NO LO VUELVAS A PREGUNTAR! Reconoce que ya lo tienes y pasa al siguiente.
2. **EMPATÍA Y RECONOCIMIENTO**: Cada vez que el usuario te dé un dato, reconócelo brevemente: "Entiendo, ${userName}, si no tienen web entonces el primer paso es...".
3. **BIFURCACIÓN DE SERVICIO (MANDATORIA)**: Si el usuario tiene Nombre, Negocio y Actividad, pero NO tiene definido "knowsTargetService", tu PREGUNTA OBLIGATORIA al final de tu mensaje debe ser: "¿Ya sabes qué servicio puntual de Hitpoly necesitas para tu crecimiento o prefieres que sigamos con el diagnóstico estratégico para descubrirlo?".
4. **FORMATO JSON**: Responde solo con JSON válido. El campo "reply" es un arreglo de mensajes cortos.`;

    return `
### [BLOQUE 1 — IDENTIDAD]
Eres ${activePersona.name}, consultor senior de Hitpoly especializado en estrategia digital.
${knowledgePrompt}

${missionBlock}

${rulesBlock}

### [BLOQUE 3 — ESTADO ACTUAL DEL CLIENTE (USAR PARA EVITAR REDUNDANCIA)]
${JSON.stringify(sanitizeAnswers(nextAnswers), null, 2)}

### [BLOQUE 4 — FORMATO TÉCNICO]
Responde Únicamente con JSON válido. El campo "reply" DEBE ser un arreglo de strings (1-2 mensajes):
{
  "reply": ["mensaje de empatia", "pregunta o transicion"],
  "extracted": {
    "userName": "",
    "userEmail": "",
    "userPhone": "",
    "knowsTargetService": "si/no",
    "businessType": "",
    "negocio": "",
    "offer": "",
    "objetivoPrincipal": "",
    "isFaqCompleted": "true (si el usuario dice que no necesita mas ayuda)"
  }
}

MENSAJE DEL USUARIO EN ESTE TURNO:
${userText}
`;
  };

  const getAssistantTurn = async (userText, currentAnswers, context = {}) => {
    const localExtracted = inferFieldsFromMessage(userText, currentAnswers, context);
    const localAnswers = mergeAnswers(currentAnswers, localExtracted, context);
    const snapshot = buildAssistantSnapshot(localAnswers);
    const messagesCount = Array.isArray(context.history) ? context.history.length : 0;

    if (Date.now() < providerCooldownUntil) {
      return {
        nextAnswers: localAnswers,
        reply: `${buildDeterministicReply(localAnswers, localExtracted, { ...context, userMessage: userText })} Por ahora seguire en modo local porque Cloudflare AI no respondio en este turno.`,
        readyForDiagnosis: hasEnoughForDiagnosis(localAnswers, messagesCount),
        snapshot,
      };
    }

    try {
      const { raw } = await runProviderWithFallbackModels(
        buildTurnPrompt(userText, localAnswers),
        0.45,
        VIRTUAL_ASSISTANT_API_CONFIG.turnModel,
        'turn'
      );
      const parsed = parseAiPayload(raw);
      const nextAnswers = mergeAnswers(localAnswers, parsed?.extracted || {}, context);
      const nextSnapshot = buildAssistantSnapshot(nextAnswers);
      let providerReply = Array.isArray(parsed?.reply) ? parsed.reply : [String(parsed?.reply || '').trim()];
      const localReply = buildDeterministicReply(nextAnswers, { ...localExtracted, ...(parsed?.extracted || {}) }, { ...context, userMessage: userText });

      // Mas permisivos: solo evitamos si esta vacio o es un error obvio
      const shouldUseProviderReply = providerReply.some(line => line.length > 3) && !raw.includes('FAILED') && !raw.includes('ERROR');

      return {
        nextAnswers,
        reply: shouldUseProviderReply ? providerReply : localReply,
        readyForDiagnosis: hasEnoughForDiagnosis(nextAnswers, messagesCount),
        snapshot: nextSnapshot,
      };
    } catch (error) {
      setProviderCooldownUntil(Date.now() + extractRetryMs(error?.message));
      return {
        nextAnswers: localAnswers,
        reply: `${buildDeterministicReply(localAnswers, localExtracted, { ...context, userMessage: userText })} Continuamos en modo local.`,
        readyForDiagnosis: hasEnoughForDiagnosis(localAnswers, messagesCount),
        snapshot,
      };
    }
  };

  const runDiagnosis = async (nextAnswers) => {
    setIsThinking(true);
    const fallback = buildFallbackDiagnosis(nextAnswers);
    const nextMetrics = buildAssistantMetrics(nextAnswers);
    const prompt = `
Actúa como un Consultor Estratégico Senior de Hitpoly. Tu objetivo es entregar una recomendación de alta fidelidad basada únicamente en los datos proporcionados.

DATOS FINALES CONFIRMADOS: ${JSON.stringify(nextAnswers, null, 1)}
MÉTRICAS CALCULADAS: ${JSON.stringify(nextMetrics, null, 1)}

### REGLAS DE ORO DEL INFORME:
1. **PROHIBIDO ALUCINAR**: Si el usuario dijo que su ticket es de $1,000, NO uses $50. Si no tienes un dato, di que es un estimado conservador basado en su mercado.
2. **NO SEAS UN JUEZ**: En lugar de decir "Su negocio no es viable", di "Para que esta estrategia sea altamente rentable, necesitamos ajustar [X factor]".
3. **ENFOQUE EN MARGENES**: Habla de CPL máximo, Retorno de Inversión y escalabilidad.
4. **ESTRUCTURA JSON EXACTA**: {"intro":"", "analysis":"", "moneyRead":"", "recommendations":["frase 1", "frase 2"], "closing":""}
5. **STRINGS SOLAMENTE**: El arreglo "recommendations" DEBE ser un arreglo de strings simples, NO objetos.
6. **TONO**: Profesional, optimista y estratégico.
`;

    if (Date.now() < providerCooldownUntil) {
      pushAssistantMessage(fallback.intro, nextAnswers, fallback);
      pushAssistantMessage(fallback.analysis, nextAnswers, fallback);
      pushAssistantMessage(fallback.moneyRead, nextAnswers, fallback);
      setIsThinking(false); setHasConclusion(true); return;
    }

    try {
      const { raw } = await runProviderWithFallbackModels(prompt, 0.35, VIRTUAL_ASSISTANT_API_CONFIG.diagnosisModel, 'diagnosis');
      const parsed = parseAiPayload(raw);
      const ensureString = (val) => {
        if (typeof val === 'string') return val;
        if (!val) return '';
        if (typeof val === 'object') {
           return `${val.title || val.name || ''} ${val.actions || val.details || ''} ${val.expectedImpact || val.impacto || ''}`.trim() || JSON.stringify(val);
        }
        return String(val);
      };

      // A5: Aplicar ensureString ANTES de guardar en el objeto diagnosis (previene crash de React)
      const diagnosis = {
        intro: ensureString(parsed?.intro || fallback.intro),
        analysis: ensureString(parsed?.analysis || fallback.analysis),
        moneyRead: ensureString(parsed?.moneyRead || fallback.moneyRead),
        recommendations: Array.isArray(parsed?.recommendations)
          ? parsed.recommendations.slice(0, 4).map(r => ensureString(r))
          : fallback.recommendations.map(r => ensureString(r)),
        closing: ensureString(parsed?.closing || fallback.closing),
      };

      pushAssistantMessage(ensureString(diagnosis.intro), nextAnswers, diagnosis);
      pushAssistantMessage(ensureString(diagnosis.analysis), nextAnswers, diagnosis);
      pushAssistantMessage(ensureString(diagnosis.moneyRead), nextAnswers, diagnosis);
      diagnosis.recommendations.forEach(r => pushAssistantMessage(ensureString(r), nextAnswers, diagnosis));
      const finalDiagnosis = ensureString(diagnosis.closing);
      pushAssistantMessage(finalDiagnosis, nextAnswers, diagnosis);

      // MARCAR SESIÓN COMO COMPLETADA EN DB
      if (sessionId) {
        fetch('https://apiweb.hitpoly.com/ajax/chatAssistantController.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            funcion: 'finalizar_diagnostico', 
            session_id: sessionId, 
            diagnosis: diagnosis 
          }),
        });
      }
    } catch (error) {
      pushAssistantMessage(String(fallback.intro), nextAnswers, fallback);
    } finally {
      setIsThinking(false);
      setHasConclusion(true);
    }
  };

  const handleSend = async (textOverride = null) => {
    const trimmed = typeof textOverride === 'string' ? textOverride.trim() : draft.trim();
    if (!trimmed || isThinking) return;

    const baseAnswers = { ...answers };
    const lastAssistantMessage = [...messages].reverse().find((message) => message.role === 'assistant')?.text || '';

    // Guardar mensaje de usuario en DB
    saveChatMessage(sessionId, 'user', trimmed);

    setMessages((prev) => {
      const nextMessages = [
        ...prev,
        {
          role: 'user',
          id: `user-${prev.length + 1}`,
          text: trimmed,
          status: 'sent',
        },
      ];
      syncMemory(nextMessages, baseAnswers);
      return nextMessages;
    });

    setDraft('');
    setIsThinking(true);
    setMessages((prev) => prev.map((m, i) => i === prev.length - 1 && m.role === 'user' ? { ...m, status: 'read' } : m));

    const turn = await getAssistantTurn(trimmed, baseAnswers, {
      lastAssistantMessage,
      userMessage: trimmed,
      history: messages
    });
    setAnswers(turn.nextAnswers);

    // BIFURCACIÓN: SI EL USUARIO YA NO NECESITA MÁS AYUDA, RESETEAR EL CHAT SIN RECARGAR
    if (turn.nextAnswers.isFaqCompleted === 'true') {
        setTimeout(() => {
            resetChat();
        }, 3000);
    }

    

    const ensureString = (val) => {
        if (typeof val === 'string') return val;
        if (!val) return '';
        if (typeof val === 'object') {
           return `${val.title || val.name || val.mensaje || ''} ${val.actions || val.details || ''}`.trim();
        }
        return String(val);
    };

    const replies = Array.isArray(turn.reply) ? turn.reply : [turn.reply];
    for (let i = 0; i < replies.length; i++) {
        const replyText = ensureString(replies[i]);
        if (!replyText.trim()) continue;
        setIsThinking(true);
        await sleep(getTypingDelay(replyText, 250));
        pushAssistantMessage(replyText, turn.nextAnswers);
        setIsThinking(false);
        if (i < replies.length - 1) await sleep(400);
    }

    if (turn.readyForDiagnosis) {
      setTimeout(() => {
        pushAssistantMessage(
          'Ya tengo suficiente para cruzar tus respuestas con la calculadora y con nuestros servicios. Dame un momento.',
          turn.nextAnswers
        );
        runDiagnosis(turn.nextAnswers);
      }, 250);
    }

    if (turn.nextAnswers.isInvestigating === 'true' && turn.nextAnswers.isFaqCompleted === 'true') {
      setTimeout(() => {
        handleClose();
      }, 5000);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const resetChat = async (closedByUser = false) => {
    // Cerrar sesión activa en la DB si no tenía diagnóstico
    if (sessionId && !hasConclusion) {
      try {
        await fetch('https://apiweb.hitpoly.com/ajax/chatAssistantController.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            funcion: 'finalizar_diagnostico', 
            session_id: sessionId, 
            diagnosis: { terminated: true } 
          }),
        });
      } catch (e) { /* silenciar si CORS falla en local */ }
    }

    // Limpiar memoria local
    clearAssistantMemory();

    // Elegir nueva persona rotando
    const sessionCount = parseInt(localStorage.getItem('hitpoly_assistant_session_count') || '0', 10);
    const nextCount = sessionCount + 1;
    localStorage.setItem('hitpoly_assistant_session_count', nextCount.toString());
    const index = sessionCount % AGENT_PERSONAS.length;
    const basePersona = AGENT_PERSONAS[index];
    const newPersona = { ...basePersona, name: `${basePersona.name} ${nextCount}` };

    // Resetear todo el estado del componente
    setMessages([]);
    setAnswers(createInitialAnswers());
    setHasConclusion(false);
    setSessionId(null);
    setDraft('');
    setActivePersona(newPersona);

    // Guardar nueva sesión vacía
    await saveAssistantMemory({
      ...createEmptyAssistantMemory(),
      messages: [],
      answers: createInitialAnswers(),
      persona: newPersona,
    }, user?.id);

    // Mostrar mensaje de bienvenida del nuevo asesor
    setIsThinking(true);
    await sleep(2000);
    pushAssistantMessage(
      `Hola, un gusto saludarle. Soy ${newPersona.name}, su asesor especializado en Hitpoly. ¿Con quién tengo el gusto de hablar? Me gustaría saber su nombre para asesorarle mejor.`,
      createInitialAnswers()
    );
    setIsThinking(false);
  };

  const handleReset = () => resetChat(true);

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box sx={{
         position: 'fixed',
         bottom: { xs: 0, sm: 24 },
         right: { xs: 0, sm: 24 },
         width: { xs: '100%', sm: 380 },
         height: { xs: 'calc(100dvh - 65px)', sm: 600 },
         maxHeight: { xs: 'calc(100dvh - 65px)', sm: '85vh' },
         backgroundColor: '#ffffff',
         borderRadius: { xs: 0, sm: '16px' },
         boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
         display: 'flex',
         flexDirection: 'column',
         overflow: 'hidden',
         zIndex: 99999
      }}>
        <Box sx={shellSx}>
          <Box sx={chatWindowSx}>
            <Box sx={headerSx}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar 
                  src={activePersona.avatar} 
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    border: '2px solid #22c55e',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.2)'
                  }} 
                />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#111111', lineHeight: 1.2 }}>
                    {activePersona.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.3, fontWeight: 500 }}>
                    {activePersona.role}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e', boxShadow: '0 0 4px #22c55e' }} />
                      <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase' }}>
                        En línea ahora
                      </Typography>
                    </Box>
                    <Box 
                      component="button"
                      onClick={handleReset}
                      sx={{ 
                        border: 'none',
                        background: 'rgba(239, 68, 68, 0.08)',
                        color: '#ef4444',
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        width: 'fit-content',
                        textTransform: 'uppercase',
                        transition: 'all 0.2s',
                        '&:hover': { background: 'rgba(239, 68, 68, 0.15)' }
                      }}
                    >
                      Terminar chat
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box display="flex" alignItems="center">
                <IconButton onClick={handleClose} sx={{ color: '#94a3b8', '&:hover': { color: '#111111' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={bodySx}>
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: '#f8fafc', 
                borderRadius: '12px', 
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600, lineHeight: 1.5, textAlign: 'center' }}>
                  Hola. Estoy aquí para analizar su modelo de negocio y ayudarle a trazar la estrategia de crecimiento exacta que necesita hoy.
                </Typography>
              </Box>

              {/* BOTONES DE RESPUESTA RAPIDA JUSTO DEBAJO DEL BANNER */}
              {messages.length <= 1 && !hasConclusion && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                {QUICK_REPLIES.map((text, idx) => (
                    <Chip
                      key={`quick-${idx}-${text}`}
                      label={text}
                      onClick={() => handleSend(text)}
                      sx={{
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        fontWeight: 700,
                        fontSize: '13px',
                        py: 2,
                        border: '1px solid #e2e8f0',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#e2e8f0', color: '#0f172a' }
                      }}
                    />
                  ))}
                </Box>
              )}

              <Box display="grid" gap={1.5}>
                {messages.map((message, idx) => (
                  <Box key={message.id || `msg-${idx}-${message.role}`} sx={{
                    display: 'flex', 
                    gap: 1.5, 
                    alignItems: 'flex-end', 
                    justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end'
                  }}>
                    {message.role === 'assistant' && (
                       <Avatar src={activePersona.avatar} sx={{ width: 32, height: 32 }} />
                    )}
                    <Box sx={message.role === 'assistant' ? assistantBubbleSx : userBubbleSx}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text)}
                      </Typography>
                      {message.role === 'user' && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mb: -0.5, mr: -0.5 }}>
                           <DoneAllIcon sx={{ fontSize: 16, color: message.status === 'read' ? '#3b82f6' : '#9ca3af' }} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}

                {isThinking ? (
                  <Box sx={assistantBubbleSx}>
                    <Typography variant="body1" color="#111111">
                      Escribiendo{typingDots}
                    </Typography>
                  </Box>
                ) : null}
                
                <Box ref={endRef} />
              </Box>
            </Box>

            <Box sx={composerSx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1.5,
                  alignItems: 'end',
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={8}
                  placeholder={hasConclusion ? 'Puedes seguir preguntando o corregir datos para recalcular.' : 'Escribe tu mensaje...'}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={isThinking}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      backgroundColor: '#f8fafc',
                    },
                  }}
                />
                <IconButton
                  onClick={handleSend}
                  disabled={!draft.trim() || isThinking}
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '18px',
                    backgroundColor: '#111111',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#000000',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#d4d4d8',
                      color: '#ffffff',
                    },
                  }}
                >
                  <SendRoundedIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default PopupCapturaLeads;
